/**
 * WebMediaViewer - Web-specific fullscreen lightbox for images and videos.
 * Uses Radix Dialog for proper stacking with other dialogs/panels.
 *
 * Features:
 * - Scroll-wheel zoom for images (1x to 5x)
 * - Drag to pan when zoomed
 * - Double-click to toggle fit / 100% zoom
 * - Download button
 * - Escape to close
 */

import { useState, useRef, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Icon } from "./Icon";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.15;

interface WebMediaViewerProps {
  /** URL of the media to display */
  src: string;
  /** Type of media */
  type: "image" | "video";
  /** Alt text for images */
  alt?: string;
  /** Callback when viewer is closed */
  onClose: () => void;
}

export function WebMediaViewer({ src, type, alt = "", onClose }: WebMediaViewerProps) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });

  const isZoomed = scale > 1;

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale((prev) => {
      const next = prev - e.deltaY * ZOOM_STEP * 0.01;
      const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
      if (clamped <= 1) setTranslate({ x: 0, y: 0 });
      return clamped;
    });
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      resetZoom();
    } else {
      setScale(2.5);
    }
  }, [scale, resetZoom]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isZoomed) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    translateStart.current = { ...translate };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [isZoomed, translate]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    setTranslate({
      x: translateStart.current.x + (e.clientX - dragStart.current.x),
      y: translateStart.current.y + (e.clientY - dragStart.current.y),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Extract filename from URL
      const urlPath = new URL(src).pathname;
      a.download = urlPath.split("/").pop() || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab
      window.open(src, "_blank");
    }
  }, [src]);

  // Click on backdrop (not on image) closes when not zoomed
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isZoomed) {
      onClose();
    }
  }, [isZoomed, onClose]);

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/90" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[100] flex items-center justify-center focus:outline-none"
          aria-describedby={undefined}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Top bar buttons */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            {/* Download button */}
            <button
              onClick={handleDownload}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Download"
            >
              <Icon name="arrow-down-to-bracket" size={18} className="text-white" />
            </button>

            {/* Close button */}
            <DialogPrimitive.Close asChild>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Close"
              >
                <Icon name="xmark" size={20} className="text-white" />
              </button>
            </DialogPrimitive.Close>
          </div>

          {/* Hidden title for accessibility */}
          <DialogPrimitive.Title className="sr-only">
            Media viewer
          </DialogPrimitive.Title>

          {/* Media content */}
          {type === "image" ? (
            <div
              className="w-full h-full flex items-center justify-center overflow-hidden"
              style={{ cursor: isZoomed ? "grab" : "zoom-in" }}
              onWheel={handleWheel}
              onDoubleClick={handleDoubleClick}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onClick={handleBackdropClick}
            >
              <img
                src={src}
                alt={alt}
                className="max-w-[90vw] max-h-[90vh] object-contain select-none"
                style={{
                  transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                  transition: isDragging.current ? "none" : "transform 0.15s ease-out",
                }}
                draggable={false}
              />
            </div>
          ) : (
            <video
              src={src}
              controls
              autoPlay
              className="max-w-[90vw] max-h-[90vh]"
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
